import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { userTickerListApi, UserTickerList } from '../services/userTickerListApi';
import { userApi } from '../services/userApi';
import {
  cacheUserLists,
  getCachedUserLists,
  clearUserListsCache,
  cacheCurrentListId,
  getCachedCurrentListId,
  cacheCurrentListName,
  generateUniqueListName,
  setCacheInvalidation,
  isCacheInvalidated,
} from '../utils/listCache';

interface UseUserListManagementParams {
  isAuthenticated: boolean;
  tickerList: string[];
  listName: string;
  setTickerList: (list: string[]) => void;
  setListName: (name: string) => void;
  refreshUsageLimitsRef: React.MutableRefObject<(() => void) | null>;
  returnPath: string;
}

interface UseUserListManagementReturn {
  // State
  userLists: UserTickerList[];
  currentListId: number | null;
  isLoadingUserLists: boolean;
  
  // Setters
  setUserLists: React.Dispatch<React.SetStateAction<UserTickerList[]>>;
  setCurrentListId: React.Dispatch<React.SetStateAction<number | null>>;
  
  // Refs (exposed for useModals)
  needsUserListsReload: React.MutableRefObject<boolean>;
  needsListDataReload: React.MutableRefObject<boolean>;
  
  // Handlers
  handleListChange: (listId: number) => Promise<void>;
  handleCreateNewList: () => Promise<void>;
  handleCreateNewListWithName: (customListName: string) => Promise<void>;
}

/**
 * Hook for managing user list state and operations across List* pages.
 * Handles loading lists, switching lists, creating lists, and loading list tickers.
 */
export const useUserListManagement = ({
  isAuthenticated,
  tickerList,
  listName,
  setTickerList,
  setListName,
  refreshUsageLimitsRef,
  returnPath,
}: UseUserListManagementParams): UseUserListManagementReturn => {
  const navigate = useNavigate();
  
  // State
  const [userLists, setUserLists] = useState<UserTickerList[]>([]);
  const [currentListId, setCurrentListId] = useState<number | null>(() => {
    // For authenticated users, try to get cached list ID first
    if (isAuthenticated) {
      const cachedListId = getCachedCurrentListId();
      if (cachedListId) {
        return cachedListId;
      }
    }
    return null;
  });
  const [isLoadingUserLists, setIsLoadingUserLists] = useState(false);
  
  // Refs to track when we need to reload data
  const needsUserListsReload = useRef(false);
  const needsListDataReload = useRef(false);
  const hasLoadedUserLists = useRef(false);
  const previousAuthState = useRef(isAuthenticated);

  // Detect when user logs in to mark that we need to reload user lists
  useEffect(() => {
    if (isAuthenticated && !previousAuthState.current) {
      console.log('🔐 User logged in, marking user lists for reload');
      needsUserListsReload.current = true;
      hasLoadedUserLists.current = false;
    }
    previousAuthState.current = isAuthenticated;
  }, [isAuthenticated]);

  // Load user lists when authenticated (only when needed)
  useEffect(() => {
    const loadUserLists = async () => {
      if (!isAuthenticated) return;
      
      // Check if we need to reload user lists
      const shouldReload = needsUserListsReload.current || !hasLoadedUserLists.current;
      
      // Try to get cached data first
      const cachedLists = getCachedUserLists();
      // getCachedUserLists returns null if cache is invalidated or expired
      // If cachedLists is null, we need to reload from API
      const cacheInvalidated = cachedLists === null;
      
      // If cache is invalidated, force reload
      if (cacheInvalidated && hasLoadedUserLists.current) {
        console.log('🔄 Cache invalidated, forcing reload from API');
        needsUserListsReload.current = true;
        hasLoadedUserLists.current = false;
      }
      
      const finalShouldReload = needsUserListsReload.current || !hasLoadedUserLists.current;
      if (!finalShouldReload) return;
      
      // Try to get cached data first (only if not invalidated)
      if (cachedLists && !needsUserListsReload.current) {
        console.log('📦 Using cached user lists');
        setUserLists(cachedLists);
        hasLoadedUserLists.current = true;
        
        // If currentListId is already set (from cache during initialization), verify it exists
        if (currentListId !== null) {
          const listExists = cachedLists.some(list => list.id === currentListId);
          if (listExists) {
            console.log('✅ Cached list ID exists in cached lists:', currentListId);
            // Current list ID is valid, keep it
            return;
          } else {
            console.log('⚠️ Cached list ID does not exist in cached lists, will load from API');
            // The cached list ID doesn't exist in cached lists, need to reload from API
            needsUserListsReload.current = true;
            // Don't return - continue to load from API
          }
        }
        
        // If we have lists and no current list selected, try to get the user's default list
        if (cachedLists.length > 0 && currentListId === null) {
          try {
            const defaultListResponse = await userApi.getDefaultList();
            if (defaultListResponse.defaultUserTickerListId) {
              // Verify the default list ID exists in cached lists
              const defaultListExists = cachedLists.some(list => list.id === defaultListResponse.defaultUserTickerListId);
              if (defaultListExists) {
                console.log('🎯 Using cached user default list:', defaultListResponse.defaultUserTickerListId);
                setCurrentListId(defaultListResponse.defaultUserTickerListId);
                cacheCurrentListId(defaultListResponse.defaultUserTickerListId);
              } else {
                console.log('⚠️ Default list ID does not exist in cached lists, using first cached list');
                setCurrentListId(cachedLists[0].id);
                cacheCurrentListId(cachedLists[0].id);
              }
            } else {
              console.log('📋 No default list set, using first cached list');
              setCurrentListId(cachedLists[0].id);
              cacheCurrentListId(cachedLists[0].id);
            }
          } catch (error) {
            console.log('⚠️ Failed to get default list from cache, using first list:', error);
            setCurrentListId(cachedLists[0].id);
            cacheCurrentListId(cachedLists[0].id);
          }
        }
        
        // Only return if we didn't need to force a reload
        if (!needsUserListsReload.current) {
          return;
        }
      }
      
      console.log('🌐 Loading user lists from API');
      setIsLoadingUserLists(true);
      try {
        const lists = await userTickerListApi.getUserLists();
        setUserLists(lists);
        cacheUserLists(lists);
        hasLoadedUserLists.current = true;
        needsUserListsReload.current = false;
        
        // Check if the cached currentListId is still valid (exists in the loaded lists)
        if (currentListId !== null) {
          const listExists = lists.some(list => list.id === currentListId);
          if (listExists) {
            console.log('✅ Cached list ID is still valid:', currentListId);
            // Keep the current list ID - it's valid
            return;
          } else {
            console.log('⚠️ Cached list ID no longer exists, selecting default list');
            // The cached list ID doesn't exist anymore, so we need to select a new one
            setCurrentListId(null);
          }
        }
        
        // If we have lists and no current list selected, try to get the user's default list
        if (lists.length > 0 && currentListId === null) {
          try {
            const defaultListResponse = await userApi.getDefaultList();
            if (defaultListResponse.defaultUserTickerListId) {
              // Verify the default list ID exists in the loaded lists
              const defaultListExists = lists.some(list => list.id === defaultListResponse.defaultUserTickerListId);
              if (defaultListExists) {
                console.log('🎯 Using user default list:', defaultListResponse.defaultUserTickerListId);
                setCurrentListId(defaultListResponse.defaultUserTickerListId);
                cacheCurrentListId(defaultListResponse.defaultUserTickerListId);
              } else {
                console.log('⚠️ Default list ID does not exist in loaded lists, using first list');
                setCurrentListId(lists[0].id);
                cacheCurrentListId(lists[0].id);
              }
            } else {
              console.log('📋 No default list set, using first list');
              setCurrentListId(lists[0].id);
              cacheCurrentListId(lists[0].id);
            }
          } catch (error) {
            console.log('⚠️ Failed to get default list, using first list:', error);
            setCurrentListId(lists[0].id);
            cacheCurrentListId(lists[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to load user lists:', error);
      } finally {
        setIsLoadingUserLists(false);
      }
    };

    loadUserLists();
  }, [isAuthenticated, currentListId]);

  /**
   * Merge tickers from sessionStorage (saved by another tab when adding a stock)
   * with tickers from API/cache. Only used for unauthenticated users.
   * For authenticated users we use server as the single source of truth and do not merge from sessionStorage.
   */
  const mergeWithSessionStorageTickers = useCallback((sourceTickers: string[], isAuth: boolean): string[] => {
    if (isAuth) {
      return sourceTickers;
    }
    try {
      const sourceSet = new Set(sourceTickers.map(t => t.toUpperCase()));
      const merged: string[] = [];

      const addFromStorage = (stored: string | null) => {
        if (!stored) return;
        try {
          const tickers: string[] = JSON.parse(stored);
          if (!Array.isArray(tickers)) return;
          for (const t of tickers) {
            if (t && !sourceSet.has(String(t).toUpperCase())) {
              merged.push(t);
              sourceSet.add(String(t).toUpperCase());
            }
          }
        } catch {
          /* ignore */
        }
      };

      if (currentListId !== null) {
        addFromStorage(sessionStorage.getItem(`stocktable-ticker-list-${currentListId}`));
      }
      addFromStorage(sessionStorage.getItem('stocktable-ticker-list'));

      for (const t of sourceTickers) {
        merged.push(t);
      }
      return merged;
    } catch {
      return sourceTickers;
    }
  }, [currentListId]);

  // Load tickers for the current list when authenticated. Always fetch from server (no list cache).
  useEffect(() => {
    const loadCurrentListTickers = async () => {
      if (!isAuthenticated || currentListId === null) return;

      try {
        const listWithTickers = await userTickerListApi.getListWithTickers(currentListId);
        const tickerSymbols = listWithTickers.tickers.map(t => t.ticker_symbol);
        const tickers = mergeWithSessionStorageTickers(tickerSymbols, isAuthenticated);
        setTickerList(tickers);
        setListName(listWithTickers.list_name);
        cacheCurrentListName(listWithTickers.list_name);
        needsListDataReload.current = false;
        if (isAuthenticated) {
          sessionStorage.removeItem('stocktable-ticker-list');
          sessionStorage.removeItem(`stocktable-ticker-list-${currentListId}`);
        }
      } catch (error) {
        console.error('Failed to load list tickers:', error);
      }
    };

    loadCurrentListTickers();
  }, [isAuthenticated, currentListId, setTickerList, setListName, mergeWithSessionStorageTickers]);

  // Handle switching to a different user list
  const handleListChange = useCallback(async (listId: number) => {
    console.log('🔄 Switching to list', listId);
    
    // Clear sessionStorage ticker list keys so we never merge stale data into the new list.
    // For authenticated users the server is the source of truth; clearing avoids cross-device confusion.
    sessionStorage.removeItem('stocktable-ticker-list');
    if (currentListId !== null) {
      sessionStorage.removeItem(`stocktable-ticker-list-${currentListId}`);
    }
    
    // Find the list name for tracking
    const selectedList = userLists.find(list => list.id === listId);
    if (selectedList) {
      const { trackListEvent } = require('../utils/analytics');
      trackListEvent('switch', selectedList.list_name);
    }
    
    setCurrentListId(listId);
    needsListDataReload.current = true; // Mark that we need to reload list data
    
    // Cache the new list ID to maintain selection across tabs
    cacheCurrentListId(listId);
    
    // Set this list as the user's default list
    if (isAuthenticated) {
      try {
        console.log('📌 Setting list as default:', listId);
        await userApi.updateDefaultList({ defaultUserTickerListId: listId });
        console.log('✅ Default list updated successfully');
      } catch (error) {
        console.error('❌ Failed to update default list:', error);
        // Don't throw error to prevent UI from breaking
      }
    }
  }, [isAuthenticated, currentListId]);

  // Handle creating a new list
  const handleCreateNewList = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('🔍 User not authenticated, redirecting to signup for new list creation');
      // Save current state to sessionStorage before redirecting
      sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(tickerList));
      sessionStorage.setItem('stocktable-list-name', listName);
      
      // Redirect to signup with new list creation context
      navigate('/signup', {
        state: {
          fromNewList: true,
          returnPath,
        },
      });
      return;
    }
    
    const newListName = generateUniqueListName(userLists);
    try {
      const newList = await userTickerListApi.createList(newListName);
      
      // Track list creation event
      const { trackListEvent } = require('../utils/analytics');
      trackListEvent('create', newList.list_name);
      
      setUserLists(prev => [...prev, newList]);
      setCurrentListId(newList.id);
      setListName(newList.list_name);
      setTickerList([]); // Start with empty list
      
      // Cache the new list ID and name to maintain selection across tabs
      cacheCurrentListId(newList.id);
      cacheCurrentListName(newList.list_name);
      
      // Set the new list as the user's default list
      try {
        console.log('📌 Setting new list as default:', newList.id);
        await userApi.updateDefaultList({ defaultUserTickerListId: newList.id });
        console.log('✅ New list set as default successfully');
      } catch (error) {
        console.error('❌ Failed to set new list as default:', error);
        // Don't throw error to prevent UI from breaking
      }
      
      // Mark that we need to reload user lists
      needsUserListsReload.current = true;
      
      // Also update the cached user lists to include the new list immediately
      // This helps the current tab see the new list immediately
      const currentLists = [...userLists, newList];
      cacheUserLists(currentLists);
      
      // Set cache invalidation flag AFTER updating cache
      // This ensures the invalidation timestamp is newer than the cache timestamp,
      // causing other tabs to reload from API when they check the cache
      setCacheInvalidation();
      
      // Refresh usage limits after successful creation
      if (refreshUsageLimitsRef.current) {
        refreshUsageLimitsRef.current();
      }
    } catch (error) {
      console.error('Failed to create new list:', error);
    }
  }, [isAuthenticated, tickerList, listName, userLists, setTickerList, setListName, refreshUsageLimitsRef, navigate, returnPath]);

  // Handle creating a new list with custom name
  const handleCreateNewListWithName = useCallback(async (customListName: string) => {
    try {
      const newList = await userTickerListApi.createList(customListName);
      setUserLists(prev => [...prev, newList]);
      setCurrentListId(newList.id);
      setListName(newList.list_name);
      setTickerList([]); // Start with empty list
      
      // Cache the new list ID and name to maintain selection across tabs
      cacheCurrentListId(newList.id);
      cacheCurrentListName(newList.list_name);
      
      // Set the new list as the user's default list
      try {
        console.log('📌 Setting new list as default:', newList.id);
        await userApi.updateDefaultList({ defaultUserTickerListId: newList.id });
        console.log('✅ New list set as default successfully');
      } catch (error) {
        console.error('❌ Failed to set new list as default:', error);
        // Don't throw error to prevent UI from breaking
      }
      
      // Mark that we need to reload user lists
      needsUserListsReload.current = true;
      
      // Also update the cached user lists to include the new list immediately
      // This helps the current tab see the new list immediately
      const currentLists = [...userLists, newList];
      cacheUserLists(currentLists);
      
      // Set cache invalidation flag AFTER updating cache
      // This ensures the invalidation timestamp is newer than the cache timestamp,
      // causing other tabs to reload from API when they check the cache
      setCacheInvalidation();
      
      // Refresh usage limits after successful creation
      if (refreshUsageLimitsRef.current) {
        refreshUsageLimitsRef.current();
      }
    } catch (error) {
      console.error('Failed to create new list with custom name:', error);
      throw error; // Re-throw to let the dialog handle the error
    }
  }, [userLists, setTickerList, setListName, refreshUsageLimitsRef]);

  return {
    // State
    userLists,
    currentListId,
    isLoadingUserLists,
    
    // Setters
    setUserLists,
    setCurrentListId,
    
    // Refs
    needsUserListsReload,
    needsListDataReload,
    
    // Handlers
    handleListChange,
    handleCreateNewList,
    handleCreateNewListWithName,
  };
};

